import React, { FC } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import ColorsWrapper from '@styles/colors';
import { scale, notchBottom } from '@components/frame';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import AttachedItem from '@v2/components/imageHandling/components/Header/components/AttachModal/AttachItem';
import { Popup, isPhone, isWeb } from '@helpers';
import DocTypeModal from '@v2/components/imageHandling/components/Header/components/DocTypeButton/DocTypeModal';
import SearchAttachmentPopup from '@v2/components/imageHandling/components/Header/components/AttachModal/SearchAttachmentPopup';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { AttachmentData, AttachModalProps } from '@v2/components/imageHandling/components/Header/components/AttachModal';
import { DocType, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { useManageAttachments } from '@components/common/attachButton/hooks/useManageAttachments';
import { useManageDocTypeButton } from '@v2/components/imageHandling/components/Header/components/DocTypeButton/hooks/useManageDocTypeButton';
import RemoveAttachmentModal from '@v2/components/imageHandling/components/Header/components/RemoveAttachmentModal';

interface AttachDetailsModalComponentProps
  extends Pick<AttachModalProps, 'onClose' | 'fileName' | 'imageId' | 'onAttach' | 'img' | 'C' | 'visible' | 'links'> {
  documentType: DocType;
  images: ImageDocumentStoreItem[];
  title?: string;
}

const AttachDetailsModal: FC<AttachDetailsModalComponentProps> = (props) => {
  const { C, visible, onClose, documentType, links = [], onAttach, imageId, img, images, title = i18n.t('Details') } = props;

  const { handleToggleFavoriteLink, handleStartRemovingAttachment, handleConfirmRemoveAttachment, showRemoveAttachmentModal, setShowRemoveAttachmentModal } = useManageAttachments({ img });

  const { onChangeDocType } = useManageDocTypeButton({ images, img });

  if (!visible) return null;

  const openDocTypeSelector = () => {
    Popup({
      Component: (p: any) => <DocTypeModal onClose={p.hide} selectedValue={documentType} change={onChangeDocType} />,
    });
  };

  const headerHeight = scale(50);

  return (
    <View
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: scale(700),
        borderTopLeftRadius: scale(10),
        borderTopRightRadius: scale(10),
        backgroundColor: C('Background/Light/Detail [mobile]'),
        zIndex: 20000,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <View
        style={{
          height: headerHeight,
          // justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: scale(18),
          flexDirection: 'row',
          gap: scale(5),
        }}
      >
        <View style={{ flex: 1 }}>
          <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]} numberOfLines={1} ellipsizeMode="middle">
            {title}
          </Text>
        </View>
        {/* Done */}
        <TouchableOpacity style={{ justifyContent: 'center' }} onPress={onClose}>
          <Text style={[font.Headline, { color: C('Link/Light/Special') }]}>{i18n.t('Done')}</Text>
        </TouchableOpacity>
      </View>

      {/* Content scroll */}
      <ScrollView
        contentContainerStyle={{
          paddingBottom: notchBottom + scale(40),
          paddingHorizontal: scale(12),
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Document Type */}
        <TouchableOpacity
          onPress={openDocTypeSelector}
          activeOpacity={0.8}
          style={[
            styles.rowTouchable,
            { backgroundColor: C('Background/Light/Field [mobile]'), borderRadius: scale(SCREEN_BORDER_RADIUS), paddingHorizontal: scale(10) },
          ]}
        >
          <View style={styles.rowLeft}>
            <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t(' Select Document Type')}</Text>
          </View>
          <View style={[styles.rowRight, { flexDirection: 'row', alignItems: 'center' }]}>
            <Text
              style={[font['Body'], { color: C('Label/Light/Tertiary'), marginRight: scale(6), maxWidth: '80%' }]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {documentType || i18n.t('Select…')}
            </Text>
            <Svg icon="chevronRight" width={scale(12)} height={scale(12)} color="Label/Light/Tertiary" />
          </View>
        </TouchableOpacity>

        {links.length > 0 && (
          <View style={{ height: scale(28), justifyContent: 'center', marginTop: scale(12), marginLeft: scale(8), marginBottom: scale(4) }}>
            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Attached To')}</Text>
          </View>
        )}

        {/* Single white container with links and attach button */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field [mobile]'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            marginTop: scale(links.length ? 0 : 15),
          }}
        >
          {/* Links list */}
          {links.map((l, idx) => (
            <View key={idx}>
              <AttachedItem
                noBg
                link={l}
                onToggleFavorite={() => handleToggleFavoriteLink({ scopeId: l.scopeId, scopeReferenceId: l.scopeReferenceId, img })}
                onPress={() => {}}
                onRemove={() => {
                  handleStartRemovingAttachment(l);
                }}
                isLast={idx === links.length - 1}
              />
            </View>
          ))}

          {/* Divider */}
          {links.length > 0 && (
            <View
              style={{
                width: '100%',
                height: StyleSheet.hairlineWidth,
                backgroundColor: C('Hairline/Light/No trans'),
              }}
            />
          )}

          {/* Attach link */}
          <TouchableOpacity
            onPress={() => {
              // Close current modal first
              onClose();

              // Open search in popup for attachment mode
              Popup({
                Component: (props: any) => (
                  <SearchAttachmentPopup
                    {...props}
                    imageDocument={img}
                    attachImageId={imageId}
                    fileName={title}
                    onAttachSuccess={(attachmentData: AttachmentData) => {
                      // Refresh the image data to show new attachment
                      console.log('Item attached successfully:', attachmentData);
                      // Call the original onAttach if provided for backwards compatibility
                      if (onAttach) {
                        onAttach(attachmentData);
                      }
                    }}
                  />
                ),
                width: '100%',
                useFullHeight: !isWeb,
                offsetTop: !isWeb ? 0 : undefined,
                offsetBottom: !isWeb ? 0 : undefined,
                bg: !isWeb ? 'Background/Light/System' : undefined,
                bottomSheet: !isPhone,
              });
            }}
            activeOpacity={0.8}
            style={{ paddingVertical: scale(10), paddingHorizontal: scale(6) }}
          >
            <Text
              style={[
                font['Button 1'],
                {
                  color: C('Link/Light/Primary'),
                  fontSize: scale(16),
                  fontWeight: '400',
                  paddingLeft: scale(10),
                },
              ]}
            >
              {i18n.t(links.length ? 'Attach to other items...' : 'Attach to')}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <RemoveAttachmentModal
        visible={showRemoveAttachmentModal}
        onClose={() => setShowRemoveAttachmentModal(false)}
        onConfirm={handleConfirmRemoveAttachment}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  rowTouchable: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: scale(14),
  },
  rowLeft: {
    flexShrink: 0,
  },
  rowRight: {
    flexShrink: 1,
    alignItems: 'flex-end',
  },
});

export default ColorsWrapper(AttachDetailsModal);
